import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import SprintrollIcon from "@/components/SprintrollIcon";
import SprintrollLogo from "@/components/SprintrollLogo";
import { Skeleton } from "@/components/ui/skeleton";
import Markdown from "@/components/Markdown";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";

export default function Terms() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["legal-terms-public"],
    queryFn: async () => (await api.get("/legal/terms")).data,
  });

  return (
    <div className="relative z-10 min-h-screen w-full" data-testid="terms-page">
      <SEO titleKey="seo.terms.title" descriptionKey="seo.terms.description" path="/terms" />
      <header className="sticky top-0 z-30 glass-header">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <SprintrollLogo className="h-7 w-auto" />
          </Link>
          <button
            onClick={() => navigate(-1)}
            data-testid="terms-back"
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition"
          >
            <ArrowLeft className="h-4 w-4" /> {t("common.back")}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <article className="glass-panel rounded-3xl p-8 md:p-12">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
              <div className="mt-6 space-y-2">
                {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-3 w-full" />)}
              </div>
            </div>
          ) : isError || !data ? (
            <p className="text-sm text-muted-foreground">No se pudo cargar el documento. Intenta de nuevo más tarde.</p>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400">
                Documento legal · v{data.version}
              </div>
              <div data-testid="terms-content" className="mt-2 [&_h1]:font-display [&_h1]:text-3xl [&_h1]:md:text-4xl [&_h1]:font-semibold [&_h1]:tracking-tight [&_h1]:mt-0 [&_h1]:mb-2 [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-2 [&_h3]:font-semibold [&_h3]:mt-5 [&_h3]:mb-1 [&_p]:my-3 [&_p]:text-[15px] [&_p]:leading-relaxed [&_p]:text-foreground/85 [&_ul]:my-3 [&_li]:my-1.5 [&_li]:text-[15px] [&_li]:leading-relaxed [&_li]:text-foreground/85">
                <Markdown>{data.content}</Markdown>
              </div>
            </>
          )}
        </article>
      </main>
    </div>
  );
}
